// ────────────────────────────────────────────────────────────────────────────
// Cohorts page.
//
// Shows the user's portfolio against US population distributions from the
// Federal Reserve SCF 2022 Summary Extract. All comparison data is bundled
// at build time (src/data/scf-percentiles.json) — zero runtime network.
//
// Layout: header card with the user's bucket summary + headline rank, then
// a self-similar grid of <PercentileCard>s — one per (dimension × split).
// ────────────────────────────────────────────────────────────────────────────

import { useMemo } from 'react';
import { useStore } from '../state/store';
import { TopBar } from '../components/TopBar';
import { PrivacyBanner } from '../components/PrivacyBanner';
import { GlossaryTerm } from '../components/GlossaryTerm';
import { Card, EmptyState, SectionHeader } from '../ui';
import { fmtMoney } from '../utils/format';
import {
  PercentileCard,
} from '../components/cohorts/PercentileCard';
import scfData from '../data/scf-percentiles.json';
import type { CohortDimension, CohortSplit, ScfDataset } from '../cohort/schema';
import { ageToBand, incomeToBand, lookupPercentile } from '../cohort/lookup';
import { computeUserValues, dimensionBlurb } from '../cohort/user';

const dataset = scfData as unknown as ScfDataset;

interface CardSpec {
  dimension: CohortDimension;
  split: CohortSplit;
  title?: string;
}

export function CohortsPage() {
  const { accounts, currentBalances, assumptions } = useStore();

  const age = assumptions.currentAge;
  const income =
    (assumptions.compensation?.baseAnnual ?? assumptions.grossAnnualSalary ?? 0) +
    (assumptions.compensation?.bonusAnnual ?? 0);

  const userValues = useMemo(
    () => computeUserValues(accounts, currentBalances),
    [accounts, currentBalances],
  );

  const ageBand = ageToBand(age);
  const incomeBand = incomeToBand(income);

  // Headline: user's NW percentile within their age band.
  const headlineRow = dataset.cohorts[`age:${ageBand}`]?.networth;
  const headline = headlineRow ? lookupPercentile(userValues.networth, headlineRow) : null;

  const cards: CardSpec[] = [
    { dimension: 'networth', split: { kind: 'age', band: ageBand }, title: 'Net worth — by age' },
    { dimension: 'networth', split: { kind: 'income', band: incomeBand }, title: 'Net worth — by income' },
    { dimension: 'financial_assets', split: { kind: 'age', band: ageBand }, title: 'Financial assets — by age' },
    { dimension: 'nonfin_assets', split: { kind: 'age', band: ageBand }, title: 'Non-financial assets — by age' },
    { dimension: 'retirement', split: { kind: 'age', band: ageBand }, title: 'Retirement accounts — by age' },
    { dimension: 'retirement', split: { kind: 'income', band: incomeBand }, title: 'Retirement accounts — by income' },
    { dimension: 'home_equity', split: { kind: 'age', band: ageBand }, title: 'Home equity — by age' },
    { dimension: 'liquid', split: { kind: 'age', band: ageBand }, title: 'Liquid (cash) — by age' },
    { dimension: 'networth', split: { kind: 'all' }, title: 'Net worth — vs all US households' },
  ];

  const buildAge = ageInDays(dataset.meta.buildAt);
  const buildAgeLabel =
    buildAge < 1
      ? 'updated today'
      : buildAge < 30
        ? `updated ${Math.round(buildAge)}d ago`
        : `updated ${Math.round(buildAge / 30)}mo ago`;

  return (
    <>
      <TopBar title="Cohorts" subtitle="Where you stand vs the US" showScenario={false} />
      <main className="mx-auto max-w-5xl space-y-4 px-4 py-4 pb-nav">
        <PrivacyBanner />

        {/* Header / your bucket */}
        <Card className="p-4">
          <SectionHeader title="Your bucket" />
          <div className="mt-2 flex flex-wrap items-baseline gap-x-6 gap-y-2 text-sm">
            <Stat label="Age" value={`${age} → ${ageBand}`} />
            <Stat label="Income" value={`${fmtMoney(income)} → ${incomeBand}`} />
            <Stat label="Filing" value={assumptions.filingStatus} />
            <Stat label="Net worth" value={fmtMoney(userValues.networth)} />
          </div>
          {headline && (
            <p className="mt-3 text-sm text-ink-700 dark:text-ink-200">
              Your net worth sits at the{' '}
              <span className="font-bold text-accent-700 dark:text-accent-400">
                {ordinal(headline.percentile)}
              </span>{' '}
              <GlossaryTerm term="percentile">percentile</GlossaryTerm> for{' '}
              <span className="font-medium">ages {ageBand}</span> in the{' '}
              <GlossaryTerm term="scf">Federal Reserve SCF</GlossaryTerm> ({dataset.dataYear} data,
              inflated to {dataset.dollarYear} dollars).
            </p>
          )}
        </Card>

        {/* Grid of comparison cards */}
        {age <= 0 || userValues.networth === 0 ? (
          <EmptyState
            title="Set your age and add at least one account"
            description="Cohort comparisons need your age (Assumptions) and your current account balances (Accounts)."
            action={
              <div className="flex gap-2">
                <a
                  href="#assumptions"
                  className="rounded-md bg-accent-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-accent-700"
                >
                  Open Assumptions
                </a>
                <a
                  href="#accounts"
                  className="rounded-md border border-ink-200 px-3 py-1.5 text-xs font-semibold text-ink-700 hover:border-accent-500 hover:text-accent-700 dark:border-ink-700 dark:text-ink-200"
                >
                  Open Accounts
                </a>
              </div>
            }
          />
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {cards.map((c, i) => (
              <PercentileCard
                key={i}
                dataset={dataset}
                dimension={c.dimension}
                split={c.split}
                userValue={userValues[c.dimension]}
                title={c.title}
              />
            ))}
          </div>
        )}

        {/* Methodology + provenance */}
        <Card className="p-4 text-xs leading-relaxed text-ink-600 dark:text-ink-300">
          <SectionHeader title="How this works" />
          <ul className="mt-2 space-y-1.5 list-disc pl-5">
            <li>
              Data: <GlossaryTerm term="scf">Federal Reserve SCF</GlossaryTerm>{' '}
              {dataset.dataYear} Summary Extract Public Data — {buildAgeLabel}. Public domain.{' '}
              <a
                href={dataset.meta.sourceUrl}
                className="text-accent-700 underline decoration-dotted dark:text-accent-400"
              >
                source
              </a>
            </li>
            <li>
              Percentiles are computed with weighted survey weights across all 5 SCF
              imputations and inflated from {dataset.dataYear} dollars to{' '}
              {dataset.dollarYear} dollars (×{dataset.cpiAdjustment.toFixed(2)}). Anchors:
              p10, p25, p50, p75, p90, p95, p99 — interpolated log-linearly between.
            </li>
            <li>
              Cairn never sends your data to any external service. The SCF file is
              downloaded only at build time on the developer machine; the production
              CSP blocks all runtime network access.
            </li>
            <li>
              <strong>Caveat:</strong> SCF NFIN (non-financial) includes vehicles and
              business equity, which Cairn doesn't track separately. If you have
              meaningful vehicle/business value, your "Non-financial assets" position
              will read low.
            </li>
          </ul>
          <details className="mt-3 text-[11px]">
            <summary className="cursor-pointer text-ink-500">Dimension definitions</summary>
            <div className="mt-2 space-y-1.5">
              {(
                ['networth', 'financial_assets', 'nonfin_assets', 'retirement', 'home_equity', 'liquid'] as CohortDimension[]
              ).map(d => (
                <div key={d}>
                  <span className="font-medium text-ink-800 dark:text-ink-100">
                    {humanDim(d)}:
                  </span>{' '}
                  <span className="text-ink-600 dark:text-ink-300">{dimensionBlurb(d)}</span>
                </div>
              ))}
            </div>
          </details>
        </Card>
      </main>
    </>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-ink-500 dark:text-ink-400">{label}</div>
      <div className="font-medium text-ink-800 dark:text-ink-100">{value}</div>
    </div>
  );
}

function ordinal(n: number): string {
  const v = Math.round(n);
  const last = v % 10;
  const lastTwo = v % 100;
  if (lastTwo >= 11 && lastTwo <= 13) return `${v}th`;
  if (last === 1) return `${v}st`;
  if (last === 2) return `${v}nd`;
  if (last === 3) return `${v}rd`;
  return `${v}th`;
}

function ageInDays(iso: string): number {
  return (Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24);
}

function humanDim(d: CohortDimension): string {
  switch (d) {
    case 'networth':
      return 'Net worth';
    case 'financial_assets':
      return 'Financial assets';
    case 'nonfin_assets':
      return 'Non-financial assets';
    case 'retirement':
      return 'Retirement accounts';
    case 'home_equity':
      return 'Home equity';
    case 'liquid':
      return 'Liquid';
  }
}
